import { useEffect } from 'react';

/** How often to re-check while the price-to-beat is still resolving. */
const RETRY_MS = 1500;
/** Safety ceiling so a stuck series never re-polls forever (~12s). */
const MAX_TRIES = 8;

/**
 * Wait out the window-boundary race on the price-to-beat.
 *
 * At a boundary the just-closed window settles in our store a beat *after* close,
 * so `findSeries` briefly reports `priceToBeatReady: false` (the strike isn't
 * known yet). Rather than render a provisional/wrong price-to-beat, the caller
 * shows a pending state and this hook refetches until the backend resolves it
 * (the prior settles → cache busts → the read returns the correct strike,
 * typically within ~2s). Bounded so a genuinely stuck series can't loop forever.
 *
 * `notReady` is `series && !series.priceToBeatReady`; `refetch` is the query's
 * stable refetch fn. Stops the moment `notReady` flips false.
 */
export function useSnakePriceToBeatReady(notReady: boolean, refetch: () => void): void {
  useEffect(() => {
    if (!notReady) return;
    let tries = 0;
    const id = setInterval(() => {
      tries += 1;
      refetch();
      if (tries >= MAX_TRIES) clearInterval(id);
    }, RETRY_MS);
    return () => clearInterval(id);
  }, [notReady, refetch]);
}
